import { useState } from 'react';
import Sidebar from '../components/Sidebar';

export interface Payment {
  id: string;
  customerName: string;
  weddingDate: string;
  makeupType: string;
  basePrice: number;
  additionalServices: {
    name: string;
    price: number;
  }[];
  partnerDiscount: number;
  totalAmount: number;
  deposit: number;
  balance: number;
  depositPaid: boolean;
  balancePaid: boolean;
  depositDate: string;
  balanceDate: string;
  status: string;
}

export default function MakeupPayments() {
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);

  const [payments, setPayments] = useState<Payment[]>([
    {
      id: '1',
      customerName: '김미영',
      weddingDate: '2024-03-25',
      makeupType: '본식+리허설',
      basePrice: 500000,
      additionalServices: [
        { name: '혼주 메이크업', price: 150000 },
        { name: '친구 메이크업', price: 100000 }
      ],
      partnerDiscount: 50000,
      totalAmount: 700000,
      deposit: 200000,
      balance: 500000,
      depositPaid: true,
      balancePaid: false,
      depositDate: '2024-02-15',
      balanceDate: '',
      status: '예약금 완료'
    },
    {
      id: '2',
      customerName: '이지은',
      weddingDate: '2024-04-10',
      makeupType: '본식',
      basePrice: 350000,
      additionalServices: [],
      partnerDiscount: 0,
      totalAmount: 350000,
      deposit: 100000,
      balance: 250000,
      depositPaid: true,
      balancePaid: false,
      depositDate: '2024-03-01',
      balanceDate: '',
      status: '예약금 완료'
    },
    {
      id: '3',
      customerName: '박수진',
      weddingDate: '2024-03-30',
      makeupType: '본식+리허설',
      basePrice: 500000,
      additionalServices: [
        { name: '혼주 메이크업', price: 150000 }
      ],
      partnerDiscount: 30000,
      totalAmount: 620000,
      deposit: 200000,
      balance: 420000,
      depositPaid: true,
      balancePaid: true,
      depositDate: '2024-02-20',
      balanceDate: '2024-03-28',
      status: '결제 완료'
    }
  ]);

  const handleViewReceipt = (payment: Payment) => {
    setSelectedPayment(payment);
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-auto p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">결제·정산 관리</h1>
            <p className="text-sm text-gray-600 mt-1">메이크업 비용과 결제 현황을 관리하세요</p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-pink-600 text-white px-4 py-2 rounded-lg hover:bg-pink-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line mr-2"></i>
            결제 등록
          </button>
        </div>

        {/* 통계 카드 */}
        <div className="grid grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">총 매출</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {(payments.reduce((sum, p) => sum + p.totalAmount, 0) / 10000).toFixed(0)}만원
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-2xl text-blue-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">예약금 수령</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {(payments.filter(p => p.depositPaid).reduce((sum, p) => sum + p.deposit, 0) / 10000).toFixed(0)}만원
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <i className="ri-check-line text-2xl text-green-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">잔금 수령</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {(payments.filter(p => p.balancePaid).reduce((sum, p) => sum + p.balance, 0) / 10000).toFixed(0)}만원
                </p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <i className="ri-wallet-line text-2xl text-purple-600"></i>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">미수금</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {(payments.filter(p => !p.balancePaid).reduce((sum, p) => sum + p.balance, 0) / 10000).toFixed(0)}만원
                </p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <i className="ri-time-line text-2xl text-orange-600"></i>
              </div>
            </div>
          </div>
        </div>

        {/* 결제 목록 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">고객명</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">예식일</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">메이크업 유형</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">총 금액</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">예약금</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">잔금</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {payments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-medium text-gray-900">{payment.customerName}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {payment.weddingDate}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                    {payment.makeupType}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="font-semibold text-gray-900">
                      {payment.totalAmount.toLocaleString()}원
                    </div>
                    {payment.partnerDiscount > 0 && (
                      <div className="text-xs text-green-600">
                        제휴할인 -{payment.partnerDiscount.toLocaleString()}원
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{payment.deposit.toLocaleString()}원</div>
                    {payment.depositPaid && (
                      <div className="text-xs text-green-600">{payment.depositDate}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{payment.balance.toLocaleString()}원</div>
                    {payment.balancePaid && payment.balanceDate && (
                      <div className="text-xs text-green-600">{payment.balanceDate}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      payment.status === '결제 완료' ? 'bg-green-100 text-green-800' :
                      payment.status === '예약금 완료' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleViewReceipt(payment)}
                      className="text-pink-600 hover:text-pink-800 mr-3 cursor-pointer whitespace-nowrap"
                    >
                      상세보기
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 결제 상세 모달 */}
        {selectedPayment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-8 w-full max-w-2xl">
              <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
                <h2 className="text-2xl font-bold">결제 상세 내역</h2>
                <button
                  onClick={() => setSelectedPayment(null)}
                  className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              <div className="space-y-6">
                {/* 고객 정보 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">고객 정보</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">고객명</div>
                      <div className="font-medium text-gray-900">{selectedPayment.customerName}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">예식일</div>
                      <div className="font-medium text-gray-900">{selectedPayment.weddingDate}</div>
                    </div>
                  </div>
                </div>

                {/* 금액 상세 */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">금액 상세</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between py-2">
                      <span className="text-gray-600">{selectedPayment.makeupType}</span>
                      <span className="font-medium text-gray-900">{selectedPayment.basePrice.toLocaleString()}원</span>
                    </div>
                    {selectedPayment.additionalServices.map((service, index) => (
                      <div key={index} className="flex justify-between py-2">
                        <span className="text-gray-600">{service.name}</span>
                        <span className="font-medium text-gray-900">{service.price.toLocaleString()}원</span>
                      </div>
                    ))}
                    {selectedPayment.partnerDiscount > 0 && (
                      <div className="flex justify-between py-2 text-green-600">
                        <span>제휴 할인</span>
                        <span className="font-medium">-{selectedPayment.partnerDiscount.toLocaleString()}원</span>
                      </div>
                    )}
                    <div className="flex justify-between py-3 border-t-2 border-gray-300">
                      <span className="text-lg font-semibold text-gray-900">총 금액</span>
                      <span className="text-lg font-bold text-pink-600">{selectedPayment.totalAmount.toLocaleString()}원</span>
                    </div>
                  </div>
                </div>

                {/* 결제 현황 */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">결제 현황</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">예약금</div>
                        <div className="text-sm text-gray-600">{selectedPayment.depositDate || '미입금'}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">{selectedPayment.deposit.toLocaleString()}원</div>
                        <div className={`text-sm ${selectedPayment.depositPaid ? 'text-green-600' : 'text-orange-600'}`}>
                          {selectedPayment.depositPaid ? '입금 완료' : '미입금'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">잔금</div>
                        <div className="text-sm text-gray-600">{selectedPayment.balanceDate || '미입금'}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-gray-900">{selectedPayment.balance.toLocaleString()}원</div>
                        <div className={`text-sm ${selectedPayment.balancePaid ? 'text-green-600' : 'text-orange-600'}`}>
                          {selectedPayment.balancePaid ? '입금 완료' : '미입금'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => window.print()}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-printer-line mr-2"></i>
                    영수증 출력
                  </button>
                  <button
                    onClick={() => setSelectedPayment(null)}
                    className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 whitespace-nowrap cursor-pointer"
                  >
                    확인
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 결제 등록 모달 */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">결제 등록</h2>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
              
              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">고객명</label>
                  <input 
                    type="text" 
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2" 
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">예식일</label>
                    <input 
                      type="date" 
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">메이크업 유형</label>
                    <select className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 cursor-pointer">
                      <option>본식</option>
                      <option>리허설</option>
                      <option>본식+리허설</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">기본 금액</label>
                  <input 
                    type="number" 
                    placeholder="350000"
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2" 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">제휴 할인</label>
                  <input 
                    type="number" 
                    placeholder="0"
                    className="w-full border border-gray-300 rounded-md px-3 py-2" 
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">예약금</label>
                    <input 
                      type="number" 
                      placeholder="100000"
                      required
                      className="w-full border border-gray-300 rounded-md px-3 py-2" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">예약금 입금일</label>
                    <input 
                      type="date" 
                      className="w-full border border-gray-300 rounded-md px-3 py-2" 
                    />
                  </div>
                </div>
                
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowAddForm(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap cursor-pointer"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 whitespace-nowrap cursor-pointer"
                  >
                    등록
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
